<script lang="ts">
  /**
   * The You tab: what you have played, on the shell's `ListPage` (a tab's
   * body: no header, the frame's bar names it; `docs/decisions/0054`). The
   * profile button's sheet is the app and your account (settings, how to
   * play, signing in and out); this is the numbers.
   *
   * - **Who.** One row at the top: signed in, your name, opening the profile
   *   sheet; as a guest, that scores stay on this device, with the way to
   *   sign in.
   * - **My scores.** A row that opens every personal best you have set.
   * - **Stats.** Solo, versus and versus-the-computer: a section per area and
   *   one group of rows per mode or variant that has been played. Signed in
   *   they come from the account; otherwise from this device.
   * - **On this device** (`docs/decisions/0059`). The rounds played together
   *   on this device by taking turns: simple totals and the most recent
   *   rounds. Their own section, kept apart from every number above and the
   *   records below; signed in, the list is the account's, synced across the
   *   owner's devices.
   * - **Records.** The global bests, whoever is signed in.
   *
   * Every section is the template's: a label, the groups, a note, at one
   * fixed inset. Something that has not happened yet is one quiet row that
   * says so, never a row of zeros.
   */
  import { Group, GroupNote, Icon, ListPage, MenuRow, PillButton, Row } from '@artibens/app-shell';
  import type { ListSection } from '@artibens/app-shell';
  import {
    MODE_IDS,
    buildMode,
    defaultConfigFor,
    describeModeConfig,
    formatClock,
  } from '@interlock/modes';
  import { DUEL_VARIANTS, ROOM_VARIANTS, duelVariantName } from '@interlock/protocol';

  import { localTotals, sessionHeadline } from '../local/history-io.js';
  import { account } from '../state/account.svelte.js';
  import { localHistory } from '../state/local-history.svelte.js';
  import { describeModeKey } from '../state/mode-key.js';
  import { parseModeKey } from '../state/mode-key.js';
  import { leaderFor, records } from '../state/records.svelte.js';
  import { RequestSequence } from '../state/request-sequence.js';
  import { hasAnyStats, loadAllModeStats } from '../state/solo-stats-io.js';
  import type { ModeStats, SoloStatsStore } from '../state/solo-stats-io.js';
  import { fetchAccountStats } from '../state/stats-io.js';
  import type { AccountVariantStats } from '../state/stats-io.js';
  import {
    hasAnyMatches,
    loadAllVersusAiStats,
    loadAllVersusStats,
  } from '../state/versus-stats-io.js';
  import type { VariantStats, VersusStatsStore } from '../state/versus-stats-io.js';

  const {
    onProfile,
    onMyScores,
    onSignIn,
  }: {
    /** Opens the profile sheet. */
    onProfile: () => void;
    onMyScores: () => void;
    /** Opens the account form. */
    onSignIn: () => void;
  } = $props();

  /* --- who ----------------------------------------------------------------- */

  const signedInAs = $derived(account.status === 'signed-in' ? account.player : null);

  const played = $derived(
    account.record === null
      ? 0
      : account.record.wins + account.record.losses + account.record.draws,
  );

  /* --- stats --------------------------------------------------------------- */

  const EMPTY_MODE_STATS: ModeStats = {
    gamesStarted: 0,
    gamesFinished: 0,
    linesCleared: 0,
    piecesPlaced: 0,
    timePlayedMs: 0,
  };
  const EMPTY_VARIANT_STATS: VariantStats = { played: 0, wins: 0, losses: 0, draws: 0 };

  let soloStore: SoloStatsStore = $state(loadAllModeStats());
  let versusStore: VersusStatsStore = $state(loadAllVersusStats());
  let versusAiStore: VersusStatsStore = $state(loadAllVersusAiStats());
  let source: 'account' | 'device' = $state('device');
  let loading = $state(false);

  function useLocalStores(): void {
    soloStore = loadAllModeStats();
    versusStore = loadAllVersusStats();
    versusAiStore = loadAllVersusAiStats();
    source = 'device';
  }

  function versusRowsFor(
    rows: readonly AccountVariantStats[],
    vsAi: boolean,
  ): Record<string, VariantStats> {
    return Object.fromEntries(
      rows
        .filter((row) => (row.vsAi ?? false) === vsAi)
        .map((row) => [row.variant, { played: row.wins + row.losses + row.draws, ...row }]),
    );
  }

  /**
   * The synced-stats request is asked again every time the account changes,
   * and an answer can arrive after the question has stopped being asked:
   * sign out while it is in flight and, without this, the account's stats
   * repaint over the device's a moment later and say "synced from your
   * account" to a guest. See `state/request-sequence.ts`.
   */
  const statsRequests = new RequestSequence();

  $effect(() => {
    if (account.status !== 'signed-in') {
      statsRequests.invalidate();
      loading = false;
      useLocalStores();
      return;
    }
    const ticket = statsRequests.begin();
    loading = true;
    void fetchAccountStats().then((result) => {
      if (!statsRequests.isCurrent(ticket)) return;
      loading = false;
      if (result.status !== 'ok') {
        useLocalStores();
        return;
      }
      soloStore = Object.fromEntries(
        result.value.solo.map(({ modeId, ...stats }) => [modeId, stats]),
      );
      versusStore = versusRowsFor(result.value.versus, false);
      versusAiStore = versusRowsFor(result.value.versus, true);
      source = 'account';
    });
    // Leaving the tab mid-request: the answer has no screen to land on.
    return () => {
      statsRequests.invalidate();
    };
  });

  const soloModes = $derived(
    MODE_IDS.map((id) => ({
      id,
      name: buildMode(defaultConfigFor(id)).copy.name,
      stats: soloStore[id] ?? EMPTY_MODE_STATS,
    })).filter((row) => hasAnyStats(row.stats)),
  );
  const versusVariants = $derived(
    ROOM_VARIANTS.map((variant) => ({
      variant,
      name: duelVariantName(variant),
      stats: versusStore[variant] ?? EMPTY_VARIANT_STATS,
    })).filter((row) => hasAnyMatches(row.stats)),
  );
  const versusAiVariants = $derived(
    DUEL_VARIANTS.map((variant) => ({
      variant,
      name: duelVariantName(variant),
      stats: versusAiStore[variant] ?? EMPTY_VARIANT_STATS,
    })).filter((row) => hasAnyMatches(row.stats)),
  );

  const soloRecordGroups = $derived(
    MODE_IDS.map((id) => ({
      id,
      name: buildMode(defaultConfigFor(id)).copy.name,
      entries: records.solo.flatMap((row) => {
        const config = parseModeKey(row.modeKey);
        if (config === null || config.modeId !== id) return [];
        return [
          {
            modeKey: row.modeKey,
            configLabel: describeModeConfig(config),
            formatted: row.formatted,
            username: row.username,
          },
        ];
      }),
    })).filter((group) => group.entries.length > 0),
  );
  const versusLeaders = $derived(
    ROOM_VARIANTS.map((variant) => ({
      variant,
      name: duelVariantName(variant),
      leader: leaderFor(variant),
    })),
  );

  /* --- on this device ----------------------------------------------------- */

  /** How many of the newest rounds the tab lists; the history keeps fifty. */
  const RECENT_ROUNDS = 10;
  const localRounds = $derived(localHistory.sessions);
  const localSummary = $derived(localTotals(localRounds));
  const recentRounds = $derived(localRounds.slice(0, RECENT_ROUNDS));

  const localNote = $derived(
    `Rounds taken in turns on one device. Never counted in your scores, stats or the records. ${
      account.status === 'signed-in' ? 'Synced with your account.' : 'Stored on this device only.'
    }`,
  );

  function roundDate(ms: number): string {
    return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  }

  const ownName = $derived(account.player?.username.toLowerCase() ?? null);

  function isMine(username: string): boolean {
    return ownName !== null && username.toLowerCase() === ownName;
  }

  const sections = $derived<ListSection[]>([
    {
      id: 'who',
      content: who,
      testid: 'account-panel',
      ...(account.status === 'unavailable'
        ? {
            note: 'Everything else works exactly as it does with one. Your personal bests and settings are saved on this device.',
          }
        : {}),
    },
    { id: 'scores', content: scores },
    ...(signedInAs !== null
      ? [{ id: 'record', label: 'Versus record', content: record, testid: 'account-record' }]
      : []),
    { id: 'solo', label: 'Solo', content: solo, testid: 'solo-stats-panel' },
    {
      id: 'versus',
      label: 'Versus & co-op',
      content: versus,
      testid: 'versus-stats-panel',
    },
    {
      id: 'versus-ai',
      label: 'Vs the computer',
      content: versusAi,
      testid: 'versus-ai-stats-panel',
    },
    {
      id: 'local',
      label: 'On this device',
      content: localList,
      testid: 'local-history-panel',
    },
    {
      id: 'records',
      label: 'Records',
      content: recordsList,
      note: 'Global: the best across every player, whoever is signed in right now.',
      testid: 'records-panel',
    },
  ]);
</script>

{#snippet personMark()}
  {#if signedInAs}
    {signedInAs.username.charAt(0).toUpperCase()}
  {:else}
    <Icon name="person" size={22} />
  {/if}
{/snippet}

{#snippet chevron()}<Icon name="chevronRight" size={20} />{/snippet}

{#snippet versusRows(stats: VariantStats)}
  <Row label="Played" value={stats.played} testid="stat" valueTestid="stat-value" />
  <Row label="Won" value={stats.wins} testid="stat" valueTestid="stat-value" />
  <Row label="Lost" value={stats.losses} testid="stat" valueTestid="stat-value" />
  <Row label="Drawn" value={stats.draws} testid="stat" valueTestid="stat-value" />
{/snippet}

{#snippet who()}
  {#if signedInAs}
    <div data-testid="signed-in-banner">
      <MenuRow
        label="Signed in as"
        value={signedInAs.username}
        mark={personMark}
        trailing={chevron}
        name={`Signed in as ${signedInAs.username}. Your profile`}
        opensDialog
        testid="open-profile-row"
        valueTestid="account-username"
        onclick={onProfile}
      />
    </div>
  {:else if account.status === 'unavailable'}
    <MenuRow
      label="Accounts"
      value="Not available on this server"
      mark={personMark}
      testid="account-unavailable"
    />
  {:else}
    <MenuRow
      label="Guest"
      value="Scores stay on this device"
      mark={personMark}
      testid="guest-banner"
    />
    {#if account.status === 'guest'}
      <!-- The way to sign in, on a line of its own: beside the guest's row
           it would cut the sentence that says what being a guest means. -->
      <div class="sign-in">
        <PillButton variant="light" label="Sign in" grow testid="open-sign-in" onclick={onSignIn} />
      </div>
    {/if}
  {/if}
{/snippet}

{#snippet scores()}
  <Group>
    <Row
      label="My scores"
      below="Every personal best you have set, next to the record"
      chevron
      opensDialog
      testid="open-my-scores"
      onclick={onMyScores}
    />
  </Group>
{/snippet}

{#snippet record()}
  <Group name="Versus record">
    {#if account.record !== null && played > 0}
      <Row label="Won" value={account.record.wins} />
      <Row label="Lost" value={account.record.losses} />
      <Row label="Drawn" value={account.record.draws} />
    {:else}
      <Row label="No duels played yet. Win one and it shows up here." tone="muted" />
    {/if}
  </Group>
{/snippet}

{#snippet solo()}
  <div class="stack">
    {#each soloModes as soloMode (soloMode.id)}
      <Group label={soloMode.name} testid="solo-stat-card">
        <Row
          label="Started"
          value={soloMode.stats.gamesStarted}
          testid="stat"
          valueTestid="stat-value"
        />
        <Row
          label="Finished"
          value={soloMode.stats.gamesFinished}
          testid="stat"
          valueTestid="stat-value"
        />
        <Row
          label="Lines"
          value={soloMode.stats.linesCleared}
          testid="stat"
          valueTestid="stat-value"
        />
        <Row
          label="Pieces"
          value={soloMode.stats.piecesPlaced}
          testid="stat"
          valueTestid="stat-value"
        />
        <Row
          label="Time played"
          value={formatClock(soloMode.stats.timePlayedMs)}
          testid="stat"
          valueTestid="stat-value"
        />
      </Group>
    {/each}
    {#if soloModes.length === 0}
      <Group>
        <Row
          label="No solo runs yet. Finish one and it shows up here."
          tone="muted"
          testid="solo-empty"
        />
      </Group>
    {/if}
    <GroupNote testid="stats-source-note">
      {#if loading}
        Loading your synced stats…
      {:else if source === 'account'}
        Stats synced from your account.
      {:else if account.status === 'signed-in'}
        Stats stored on this device.
      {:else}
        Stats stored on this device. Sign in to sync them.
      {/if}
    </GroupNote>
  </div>
{/snippet}

{#snippet versus()}
  <div class="stack">
    {#each versusVariants as row (row.variant)}
      <Group label={row.name} testid="versus-stat-card">
        {@render versusRows(row.stats)}
      </Group>
    {/each}
    {#if versusVariants.length === 0}
      <Group>
        <Row label="No matches against other players yet." tone="muted" testid="versus-empty" />
      </Group>
    {/if}
  </div>
{/snippet}

{#snippet versusAi()}
  <div class="stack">
    {#each versusAiVariants as row (row.variant)}
      <Group label={row.name} testid="versus-ai-stat-card">
        {@render versusRows(row.stats)}
      </Group>
    {/each}
    {#if versusAiVariants.length === 0}
      <Group>
        <Row label="No matches against the computer yet." tone="muted" testid="versus-ai-empty" />
      </Group>
    {/if}
    <GroupNote testid="versus-ai-note">
      Against the computer. Kept apart from the record above, and never counted toward the global
      leaders below.
    </GroupNote>
  </div>
{/snippet}

{#snippet localList()}
  <div class="stack">
    {#if localRounds.length === 0}
      <Group>
        <Row
          label="No rounds on one device yet"
          below="Start one from Together, On this device."
          tone="muted"
          testid="local-history-empty"
        />
      </Group>
    {:else}
      <Group label="Taking turns" testid="local-totals">
        <Row
          label="Rounds played"
          value={localSummary.rounds}
          testid="local-stat"
          valueTestid="local-stat-value"
        />
        <Row
          label="Turns played"
          value={localSummary.turns}
          testid="local-stat"
          valueTestid="local-stat-value"
        />
        {#each localSummary.wins.slice(0, 4) as entry (entry.name)}
          <Row
            label={entry.name}
            value={`${String(entry.wins)} ${entry.wins === 1 ? 'win' : 'wins'}`}
            testid="local-wins"
          />
        {/each}
      </Group>
      <Group label="Recent rounds" testid="local-recent">
        {#each recentRounds as round (round.id)}
          <Row
            label={sessionHeadline(round)}
            below={`${describeModeKey(round.modeKey)} · ${String(round.players.length)} players`}
            value={roundDate(round.playedAtMs)}
            testid="local-round"
          />
        {/each}
      </Group>
    {/if}
    <GroupNote testid="local-history-note">{localNote}</GroupNote>
  </div>
{/snippet}

{#snippet recordsList()}
  <div class="stack">
    {#if records.status === 'unknown'}
      <Group><Row label="Loading global records…" tone="muted" /></Group>
    {:else if records.status === 'unavailable'}
      <Group><Row label="Records need a connection." tone="muted" /></Group>
    {:else}
      {#each soloRecordGroups as group (group.id)}
        <Group label={group.name}>
          {#each group.entries as entry (entry.modeKey)}
            <Row
              label={entry.configLabel}
              value={`${entry.formatted} · ${entry.username}`}
              tone={isMine(entry.username) ? 'me' : 'default'}
              testid="record-row-solo"
            />
          {/each}
        </Group>
      {/each}
      {#if soloRecordGroups.length === 0}
        <Group>
          <Row
            label="Nobody has set a record yet. Be the first."
            tone="muted"
            testid="records-solo-empty"
          />
        </Group>
      {/if}

      <Group label="Versus & co-op leaders">
        {#each versusLeaders as row (row.variant)}
          {#if row.leader}
            <Row
              label={row.name}
              value={`${row.leader.username} · ${String(row.leader.wins)} ${row.leader.wins === 1 ? 'win' : 'wins'}`}
              tone={isMine(row.leader.username) ? 'me' : 'default'}
              testid="record-leader-card"
            />
          {:else}
            <Row label={row.name} value="nobody yet" tone="muted" testid="record-leader-card" />
          {/if}
        {/each}
      </Group>
    {/if}
  </div>
{/snippet}

<ListPage {sections} testid="you" />

<style>
  /* Several groups in one section: the gap the template keeps between
     blocks, so a section of groups reads as one section. */
  .sign-in {
    display: flex;
    margin-top: var(--ui-space-2);
  }

  .stack {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-3);
  }
</style>
